import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './AttendanceCheck.module.css';

const DAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function formatDatetime(v: string): string {
    if (!v) return '';
    const d = new Date(v);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} (${DAY_KO[d.getDay()]}) ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getNowDatetimeLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const AttendanceCheck = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);

    // Edit panel fields
    const [editTitle, setEditTitle] = useState('출근');
    const [editDatetime, setEditDatetime] = useState(getNowDatetimeLocal());
    const [editWorkspace, setEditWorkspace] = useState('에이아이에스');
    const [editWorker, setEditWorker] = useState('김복수');

    // Info card display values
    const [cardTitle, setCardTitle] = useState('출근');
    const [cardDate, setCardDate] = useState(
        formatDatetime(getNowDatetimeLocal()),
    );
    const [cardWorkspace, setCardWorkspace] = useState('에이아이에스');
    const [cardWorker, setCardWorker] = useState('김복수');

    // UI state
    const [applyFeedback, setApplyFeedback] = useState(false);
    const [editPanelVisible, setEditPanelVisible] = useState(true);

    const addMarker = useCallback((coord: any, mapInstance: any) => {
        // 기존 마커 제거
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        const marker = new window.naver.maps.Marker({
            position: coord,
            map: mapInstance,
            icon: {
                content: `<div style="width:32px;height:44px;"><svg width="38" height="47" viewBox="0 -5 30 50" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 24 14 24S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="#6267E9"/><path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 24 14 24S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="none" stroke="#5B4FE8" stroke-width="1.5"/><circle cx="14" cy="14" r="5.5" fill="white" opacity="0.9"/></svg></div>`,
                anchor: new window.naver.maps.Point(14, 38),
            },
        });

        markersRef.current.push(marker);
    }, []);

    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.naver || !window.naver.maps) return;

        const naverMap = new window.naver.maps.Map(mapRef.current, {
            center: new window.naver.maps.LatLng(37.5665, 126.978),
            zoom: 14,
            mapTypeControl: false,
            zoomControl: false,
        });

        mapInstanceRef.current = naverMap;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(pos => {
                naverMap.setCenter(
                    new window.naver.maps.LatLng(
                        pos.coords.latitude,
                        pos.coords.longitude,
                    ),
                );
                naverMap.setZoom(17);
            });
        }

        window.naver.maps.Event.addListener(naverMap, 'click', (e: any) => {
            addMarker(e.coord, naverMap);
        });
    }, [addMarker]);

    useEffect(() => {
        const load = () => {
            if (window.naver && window.naver.maps) {
                initializeMap();
                return;
            }
            const existing = document.querySelector(
                'script[src*="openapi.map.naver.com"]',
            );
            if (existing) existing.remove();

            const clientId = import.meta.env.VITE_NCP_API_KEY_ID;
            const script = document.createElement('script');
            script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`;
            script.async = true;
            script.onload = () => setTimeout(initializeMap, 200);
            document.head.appendChild(script);
        };
        load();
    }, [initializeMap]);

    const handleApply = () => {
        setCardTitle(editTitle);
        setCardDate(formatDatetime(editDatetime));
        setCardWorkspace(editWorkspace.trim() || '—');
        setCardWorker(editWorker.trim() || '—');
        setApplyFeedback(true);
        setTimeout(() => setApplyFeedback(false), 1400);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleApply();
    };

    return (
        <div className={styles.page}>
            {/* Edit Panel */}
            {editPanelVisible && (
                <div className={styles.editPanel}>
                    <div className={styles.editPanelTitle}>카드 정보 편집</div>
                    <div className={styles.editGrid}>
                        <div className={styles.editField}>
                            <label>구분</label>
                            <select
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                onKeyDown={handleKeyDown}
                            >
                                <option value="출근">출근</option>
                                <option value="퇴근">퇴근</option>
                                <option value="외출">외출</option>
                                <option value="복귀">복귀</option>
                            </select>
                        </div>

                        <div className={styles.editField}>
                            <label>날짜 / 시간</label>
                            <input
                                type="datetime-local"
                                value={editDatetime}
                                onChange={e => setEditDatetime(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className={styles.editField}>
                            <label>워크스페이스</label>
                            <input
                                type="text"
                                placeholder="워크스페이스명"
                                value={editWorkspace}
                                onChange={e => setEditWorkspace(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className={styles.editField}>
                            <label>근무자</label>
                            <input
                                type="text"
                                placeholder="이름"
                                value={editWorker}
                                onChange={e => setEditWorker(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div
                            className={`${styles.editField} ${styles.editFieldLast}`}
                        >
                            <div className={styles.btnGroup}>
                                <button
                                    className={`${styles.applyBtn} ${applyFeedback ? styles.applied : ''}`}
                                    onClick={handleApply}
                                >
                                    {applyFeedback ? '적용됨 ✓' : '적용하기'}
                                </button>
                                <button
                                    className={styles.hideEditBtn}
                                    onClick={() => setEditPanelVisible(false)}
                                >
                                    편집정보 닫기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Map Container */}
            <div className={styles.mapContainer}>
                <div ref={mapRef} className={styles.naverMap} />

                {/* Info Card Overlay */}
                <div className={styles.infoCard}>
                    <div className={styles.infoHeader}>
                        <span className={styles.infoTitle}>{cardTitle}</span>
                        <span className={styles.infoDate}>{cardDate}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>워크스페이스</span>
                        <span className={styles.infoText}>{cardWorkspace}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>근무자</span>
                        <span className={styles.infoText}>{cardWorker}</span>
                    </div>
                </div>

                {/* Close Button Overlay */}
                <div className={styles.closeButtonWrap}>
                    <button className={styles.closeButton}>닫기</button>
                </div>
            </div>
        </div>
    );
};

export default AttendanceCheck;
